import { useNavigate } from "react-router-dom";
import { Box, Skeleton, Typography, alpha, useTheme } from "@mui/material";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import NotificationImportantOutlinedIcon from "@mui/icons-material/NotificationImportantOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { formatCurrency } from "../../../Funcoes/formatCurrency";
import { formatNumber } from "../../../Funcoes/formatNumber";

const ACCENT = "#E8A317";

const formatOrDash = (value, formatter) => {
  if (value == null) return "—";
  return formatter(value);
};

const KpiCards = ({ kpis, loading }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === "dark";

  const cards = [
    {
      key: "sc_sem_pc",
      label: "SC sem Pedido",
      value: formatOrDash(kpis?.sc_sem_pc?.quantidade, formatNumber),
      hint: "Solicitações do período sem PC",
      path: "/modulo/controladoria/solicitacoes-compra",
      Icon: RequestQuoteOutlinedIcon,
    },
    {
      key: "pc_abertos",
      label: "PC com saldo",
      value: formatOrDash(kpis?.pc_abertos?.quantidade, formatNumber),
      hint: "Pedidos com quantidade pendente",
      path: "/modulo/controladoria/pedidos-compra",
      Icon: ShoppingCartOutlinedIcon,
    },
    {
      key: "valor_aberto",
      label: "Valor pendente",
      value:
        kpis?.desembolso_aberto?.valor == null
          ? "—"
          : `R$ ${formatCurrency(kpis.desembolso_aberto.valor)}`,
      hint: "Saldo financeiro em aberto",
      path: "/modulo/controladoria/fluxo-caixa",
      Icon: AccountBalanceWalletOutlinedIcon,
    },
    {
      key: "aguardando_nf",
      label: "Aguardando NF",
      value: formatOrDash(kpis?.pc_aguardando_nf?.quantidade, formatNumber),
      hint: "PCs do período não finalizados",
      path: "/modulo/controladoria/nfs-pendentes",
      Icon: NotificationImportantOutlinedIcon,
    },
    {
      key: "nfs_orfas",
      label: "NFs sem vínculo",
      value: formatOrDash(kpis?.nfs_sem_vinculo?.quantidade, formatNumber),
      hint:
        kpis?.nfs_sem_vinculo?.valor != null
          ? `R$ ${formatCurrency(kpis.nfs_sem_vinculo.valor)}`
          : "NFs do período sem PC",
      path: "/modulo/controladoria/consulta-nfs",
      Icon: ReceiptLongOutlinedIcon,
    },
  ];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, minmax(0, 1fr))",
          md: "repeat(3, minmax(0, 1fr))",
          xl: "repeat(5, minmax(0, 1fr))",
        },
        gap: 1.75,
        mb: 2.5,
      }}
    >
      {cards.map(({ key, label, value, hint, path, Icon }) => (
        <Box
          key={key}
          component="button"
          type="button"
          onClick={() => navigate(path)}
          sx={{
            width: "100%",
            textAlign: "left",
            cursor: "pointer",
            border: "1px solid var(--ctrl-border)",
            borderRadius: "14px",
            bgcolor: "var(--ctrl-surface)",
            color: "var(--ctrl-text)",
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            minHeight: 120,
            transition:
              "transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease",
            boxShadow: isDark
              ? "0 8px 24px rgba(0,0,0,0.22)"
              : "0 8px 22px rgba(27,58,75,0.06)",
            "&:hover": {
              transform: "translateY(-2px)",
              borderColor: alpha(ACCENT, isDark ? 0.55 : 0.7),
              boxShadow: isDark
                ? "0 12px 28px rgba(0,0,0,0.32)"
                : "0 12px 28px rgba(27,58,75,0.1)",
            },
            "&:active": { transform: "translateY(0px)" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                fontSize: 11.5,
                fontWeight: 700,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: "var(--ctrl-text-muted)",
              }}
            >
              {label}
            </Typography>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "10px",
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(ACCENT, isDark ? 0.16 : 0.14),
                color: ACCENT,
              }}
            >
              <Icon sx={{ fontSize: 18 }} />
            </Box>
          </Box>

          {loading ? (
            <Skeleton
              variant="rounded"
              height={28}
              sx={{ bgcolor: alpha(ACCENT, 0.12), maxWidth: 140 }}
            />
          ) : (
            <Typography
              sx={{
                fontSize: { xs: 18, md: 20 },
                fontWeight: 800,
                letterSpacing: "-0.02em",
                lineHeight: 1.15,
              }}
            >
              {value}
            </Typography>
          )}

          <Typography
            sx={{
              fontSize: 11.5,
              color: "var(--ctrl-text-muted)",
              lineHeight: 1.35,
            }}
          >
            {loading ? " " : hint}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default KpiCards;
